import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { makeStyles } from '~/tools/makeStyles';
import apolloClient from '~/api/apollo';
import graphql from '~/api/graphql';

const DEBOUNCE_MS = 300;
const RESULTS_LIMIT = 50;

const useStyles = makeStyles(() => ({
  combobox: {
    minWidth: 220,
    maxWidth: 280,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 4,
  },
  input: {
    fontSize: 14,
    padding: '4px 8px !important',
  },
}));

function getOptionLabel(employee) {
  const first = employee.firstName || '';
  const last = employee.lastName || '';
  return `${first} ${last}`.trim() || `Employee ${employee.id}`;
}

const PreviewEmployeeCombobox = ({ surveyCode, onChange }) => {
  const styles = useStyles();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const debounceTimer = useRef(null);

  const fetchEmployees = useCallback(
    async (search) => {
      setLoading(true);
      try {
        const { data } = await apolloClient.query({
          query: graphql.searchPreviewEmployees,
          variables: { hashCode: surveyCode, search: search || undefined, limit: RESULTS_LIMIT },
          fetchPolicy: 'network-only',
        });
        setOptions(data.previewEmployeeSearch || []);
      } catch (_err) {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [surveyCode],
  );

  useEffect(() => {
    fetchEmployees('');
    return () => clearTimeout(debounceTimer.current);
  }, [fetchEmployees]);

  const handleInputChange = (_event, value) => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => fetchEmployees(value), DEBOUNCE_MS);
  };

  const handleChange = (_event, employee) => {
    setSelectedEmployee(employee);
    onChange(employee);
  };

  return (
    <Autocomplete
      options={options}
      value={selectedEmployee}
      getOptionLabel={getOptionLabel}
      getOptionKey={(option) => option.id}
      onChange={handleChange}
      onInputChange={handleInputChange}
      filterOptions={(opts) => opts}
      loading={loading}
      clearOnEscape
      size="small"
      className={styles.combobox}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Choose employee to impersonate"
          variant="outlined"
          inputProps={{ ...params.inputProps, className: styles.input }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={14} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

PreviewEmployeeCombobox.propTypes = {
  surveyCode: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default PreviewEmployeeCombobox;
